import type {
  ContinuityDiagnostic,
  ContinuityStatus,
  EmotionalIntentSegment,
  FxIntentNode,
} from "./NarrativeSonics.types";

const ARC_GAP_SOFT_SEC = 0.55;
const ARC_GAP_HARD_SEC = 1.4;
const FX_SUPPORT_LOOKAROUND_SEC = 1.75;
const FX_DENSITY_WINDOW_SEC = 4;
const FX_CONFLICT_MIN_OVERLAP_RATIO = 0.35;
const FX_CONFLICT_MIN_STRENGTH = 0.45;

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function rangeDuration(startSec: number, endSec: number): number {
  return Math.max(0.001, endSec - startSec);
}

function scoreForStatus(status: ContinuityStatus): number {
  switch (status) {
    case "coherent":
      return 0.9;
    case "dense":
      return 0.42;
    case "mismatched":
      return 0.34;
    case "gapped":
      return 0.26;
    case "abrupt":
      return 0.22;
    case "unresolved":
      return 0.18;
    default:
      return 0.5;
  }
}

function makeDiagnostic(
  id: string,
  startSec: number,
  endSec: number,
  status: ContinuityStatus,
  warning: string,
  relatedArcIds?: string[],
  relatedFxNodeIds?: string[],
): ContinuityDiagnostic {
  return {
    id,
    startSec,
    endSec,
    status,
    warning,
    continuityScore: scoreForStatus(status),
    authoredBy: "derived",
    relatedArcIds,
    relatedFxNodeIds,
  };
}

function overlapDuration(aStart: number, aEnd: number, bStart: number, bEnd: number): number {
  return Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));
}

function gapDuration(aEnd: number, bStart: number): number {
  return Math.max(0, bStart - aEnd);
}

function normalizedOverlapRatio(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): number {
  const overlap = overlapDuration(aStart, aEnd, bStart, bEnd);
  const shorter = Math.min(rangeDuration(aStart, aEnd), rangeDuration(bStart, bEnd));
  return clamp(overlap / shorter, 0, 1);
}

function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function averageStrength(nodes: FxIntentNode[]): number {
  return average(nodes.map((node) => clamp(node.strength, 0, 1)));
}

function weightedArcShift(current: EmotionalIntentSegment, next: EmotionalIntentSegment): number {
  const deltaIntensity = Math.abs(next.intensity - current.intensity);
  const deltaUrgency = Math.abs(next.urgency - current.urgency);
  const deltaValence01 = Math.abs(next.valence - current.valence) * 0.5;
  return clamp(deltaIntensity * 0.42 + deltaUrgency * 0.34 + deltaValence01 * 0.24, 0, 1);
}

function arcsNearBoundary(
  current: EmotionalIntentSegment,
  next: EmotionalIntentSegment,
  fxNodes: FxIntentNode[],
): FxIntentNode[] {
  const fromSec = current.endSec - 0.65;
  const toSec = next.startSec + 0.65;
  return fxNodes.filter((node) => node.endSec >= fromSec && node.startSec <= toSec);
}

function findNearbyArcs(node: FxIntentNode, arcs: EmotionalIntentSegment[]): EmotionalIntentSegment[] {
  return arcs.filter(
    (arc) =>
      overlapDuration(node.startSec, node.endSec, arc.startSec, arc.endSec) > 0 ||
      (arc.startSec <= node.endSec + FX_SUPPORT_LOOKAROUND_SEC && arc.endSec >= node.startSec - FX_SUPPORT_LOOKAROUND_SEC),
  );
}

function findBestArc(node: FxIntentNode, arcs: EmotionalIntentSegment[]): EmotionalIntentSegment | null {
  let best: EmotionalIntentSegment | null = null;
  let bestScore = -1;

  for (const arc of arcs) {
    const overlap = overlapDuration(node.startSec, node.endSec, arc.startSec, arc.endSec);
    const distance = Math.max(0, Math.max(arc.startSec - node.endSec, node.startSec - arc.endSec));
    const overlapScore = overlap / rangeDuration(node.startSec, node.endSec);
    const proximityScore = clamp(1 - distance / FX_SUPPORT_LOOKAROUND_SEC, 0, 1);
    const energyScore = clamp(arc.intensity * 0.58 + arc.urgency * 0.3 + Math.max(0, arc.valence) * 0.12, 0, 1);
    const score = overlapScore * 0.58 + proximityScore * 0.26 + energyScore * 0.16;
    if (score > bestScore) {
      best = arc;
      bestScore = score;
    }
  }

  return best;
}

function nextArcAfter(timeSec: number, arcs: EmotionalIntentSegment[]): EmotionalIntentSegment | null {
  return arcs.find((arc) => arc.startSec >= timeSec) ?? null;
}

function previousArcBefore(timeSec: number, arcs: EmotionalIntentSegment[]): EmotionalIntentSegment | null {
  for (let index = arcs.length - 1; index >= 0; index -= 1) {
    if (arcs[index].endSec <= timeSec) return arcs[index];
  }
  return null;
}

export function deriveContinuityDiagnostics(
  arcSegments: EmotionalIntentSegment[],
  fxNodes: FxIntentNode[],
  durationSec?: number,
): ContinuityDiagnostic[] {
  const diagnostics: ContinuityDiagnostic[] = [];
  const sortedArcs = [...arcSegments].sort((a, b) => a.startSec - b.startSec);
  const sortedFx = [...fxNodes].sort((a, b) => a.startSec - b.startSec);
  const totalDuration = Math.max(0, Number(durationSec ?? 0));
  const flaggedFxNodeIds = new Set<string>();

  for (let index = 0; index < sortedArcs.length - 1; index += 1) {
    const current = sortedArcs[index];
    const next = sortedArcs[index + 1];
    const gapSec = gapDuration(current.endSec, next.startSec);
    const deltaIntensity = Math.abs(next.intensity - current.intensity);
    const deltaUrgency = Math.abs(next.urgency - current.urgency);
    const deltaValence = Math.abs(next.valence - current.valence);
    const boundaryFx = arcsNearBoundary(current, next, sortedFx);
    const boundaryFxStrength = averageStrength(boundaryFx);
    const compositeShift = weightedArcShift(current, next);
    const abruptThreshold = 0.56 + boundaryFxStrength * 0.18;

    if (gapSec >= ARC_GAP_HARD_SEC) {
      diagnostics.push(
        makeDiagnostic(
          `arc-gap-${current.id}-${next.id}`,
          current.endSec,
          next.startSec,
          "gapped",
          `Gap of ${gapSec.toFixed(1)}s between authored ARC regions leaves the hand-off unsupported.`,
          [current.id, next.id],
          boundaryFx.map((node) => node.id),
        ),
      );
      continue;
    }

    if (gapSec >= ARC_GAP_SOFT_SEC && boundaryFxStrength < 0.42) {
      diagnostics.push(
        makeDiagnostic(
          `arc-gap-${current.id}-${next.id}`,
          current.endSec,
          next.startSec,
          "gapped",
          `Gap of ${gapSec.toFixed(1)}s between authored ARC regions reads light on transition support.`,
          [current.id, next.id],
          boundaryFx.map((node) => node.id),
        ),
      );
      continue;
    }

    if (compositeShift > abruptThreshold || (compositeShift > 0.46 && gapSec > 0.22 && boundaryFxStrength < 0.34)) {
      diagnostics.push(
        makeDiagnostic(
          `arc-shift-${current.id}-${next.id}`,
          Math.max(current.startSec, current.endSec - 0.2),
          Math.min(next.endSec, next.startSec + 0.2),
          "abrupt",
          `ARC hand-off feels abrupt: valence ${deltaValence.toFixed(2)}, intensity ${deltaIntensity.toFixed(2)}, urgency ${deltaUrgency.toFixed(2)}.`,
          [current.id, next.id],
          boundaryFx.map((node) => node.id),
        ),
      );
      continue;
    }

    diagnostics.push(
      makeDiagnostic(
        `arc-coherent-${current.id}-${next.id}`,
        Math.max(current.startSec, current.endSec - 0.16),
        Math.min(next.endSec, next.startSec + 0.16),
        "coherent",
        boundaryFx.length > 0
          ? "Adjacent ARC regions hand off coherently with nearby FX support."
          : "Adjacent ARC regions transition coherently.",
        [current.id, next.id],
        boundaryFx.map((node) => node.id),
      ),
    );
  }

  for (let startIndex = 0; startIndex < sortedFx.length; startIndex += 1) {
    const windowStart = sortedFx[startIndex].startSec;
    const windowEnd = windowStart + FX_DENSITY_WINDOW_SEC;
    const windowNodes = sortedFx.filter(
      (node) => node.endSec >= windowStart && node.startSec <= windowEnd,
    );
    const strongNodes = windowNodes.filter((node) => node.strength >= 0.58);

    if (windowNodes.length >= 4 || (strongNodes.length >= 3 && averageStrength(strongNodes) >= 0.6)) {
      const clusterIds = windowNodes.map((node) => node.id);
      const clusterKey = `fx-dense-${clusterIds.join("-")}`;
      const clusterAlreadyExists = diagnostics.some((diagnostic) => diagnostic.id === clusterKey);
      if (!clusterAlreadyExists) {
        diagnostics.push(
          makeDiagnostic(
            clusterKey,
            Math.min(...windowNodes.map((node) => node.startSec)),
            Math.max(...windowNodes.map((node) => node.endSec)),
            "dense",
            "FX activity is stacking tightly in this window and may muddy the editorial hand-off.",
            undefined,
            clusterIds,
          ),
        );
        for (const node of windowNodes) flaggedFxNodeIds.add(node.id);
      }
    }
  }

  for (let index = 0; index < sortedFx.length; index += 1) {
    const current = sortedFx[index];
    for (let peerIndex = index + 1; peerIndex < sortedFx.length; peerIndex += 1) {
      const peer = sortedFx[peerIndex];
      if (peer.startSec > current.endSec) break;

      const overlapRatio = normalizedOverlapRatio(current.startSec, current.endSec, peer.startSec, peer.endSec);
      const isOpposingPair =
        (current.type === "duck" && peer.type === "swell") ||
        (current.type === "swell" && peer.type === "duck");

      if (
        isOpposingPair &&
        overlapRatio >= FX_CONFLICT_MIN_OVERLAP_RATIO &&
        current.strength >= FX_CONFLICT_MIN_STRENGTH &&
        peer.strength >= FX_CONFLICT_MIN_STRENGTH
      ) {
        diagnostics.push(
          makeDiagnostic(
            `fx-mismatch-${current.id}-${peer.id}`,
            Math.max(current.startSec, peer.startSec),
            Math.min(current.endSec, peer.endSec),
            "mismatched",
            `Opposing FX intents overlap too strongly: ${current.type.toUpperCase()} against ${peer.type.toUpperCase()}.`,
            undefined,
            [current.id, peer.id],
          ),
        );
        flaggedFxNodeIds.add(current.id);
        flaggedFxNodeIds.add(peer.id);
      }
    }
  }

  for (let index = 0; index < sortedFx.length; index += 1) {
    const node = sortedFx[index];
    if (flaggedFxNodeIds.has(node.id)) continue;

    const nearbyArcs = findNearbyArcs(node, sortedArcs);
    const arc = findBestArc(node, nearbyArcs);
    if (!arc) {
      diagnostics.push(
        makeDiagnostic(
          `fx-unresolved-${node.id}`,
          node.startSec,
          node.endSec,
          "unresolved",
          `FX ${node.type.toUpperCase()} sits without nearby ARC support and may read disconnected.`,
          undefined,
          [node.id],
        ),
      );
      continue;
    }

    const nextArc = nextArcAfter(node.endSec, sortedArcs);
    const previousArc = previousArcBefore(node.startSec, sortedArcs);
    const arcEnergy = clamp(arc.intensity * 0.58 + arc.urgency * 0.32 + Math.max(0, arc.valence) * 0.1, 0, 1);
    const releaseAgainstRise =
      node.type === "release" &&
      nextArc &&
      gapDuration(node.endSec, nextArc.startSec) < 0.85 &&
      nextArc.intensity > arc.intensity + 0.18 &&
      nextArc.urgency >= arc.urgency;
    const swellAgainstDrop =
      node.type === "swell" &&
      previousArc &&
      gapDuration(previousArc.endSec, node.startSec) < 0.75 &&
      arc.valence < -0.2 &&
      arc.intensity < 0.28 &&
      node.strength >= 0.56;

    if (node.type === "duck" && node.strength > arcEnergy + 0.24 && arcEnergy < 0.44) {
      diagnostics.push(
        makeDiagnostic(
          `fx-duck-mismatch-${node.id}`,
          node.startSec,
          node.endSec,
          "mismatched",
          "Duck intent is stronger than the surrounding ARC pressure currently suggests.",
          [arc.id],
          [node.id],
        ),
      );
      continue;
    }

    if ((node.type === "release" && arc.urgency > 0.76 && arc.intensity > 0.64) || releaseAgainstRise) {
      diagnostics.push(
        makeDiagnostic(
          `fx-release-unresolved-${node.id}`,
          node.startSec,
          node.endSec,
          "unresolved",
          releaseAgainstRise
            ? "Release arrives too early against the next ARC rise and may flatten the transition."
            : "Release intent lands while ARC urgency is still high.",
          [arc.id],
          [node.id],
        ),
      );
      continue;
    }

    if ((node.type === "swell" && arc.intensity < 0.22 && arc.valence < -0.18 && node.strength >= 0.58) || swellAgainstDrop) {
      diagnostics.push(
        makeDiagnostic(
          `fx-swell-mismatch-${node.id}`,
          node.startSec,
          node.endSec,
          "mismatched",
          "Swell intent appears unsupported by the surrounding ARC state and may feel unmotivated.",
          [arc.id],
          [node.id],
        ),
      );
      continue;
    }

    diagnostics.push(
      makeDiagnostic(
        `fx-coherent-${node.id}`,
        node.startSec,
        node.endSec,
        "coherent",
          `FX ${node.type.toUpperCase()} reads supported by the surrounding ARC trajectory.`,
        [arc.id],
        [node.id],
      ),
    );
  }

  if (sortedArcs.length === 0 && sortedFx.length > 0) {
    diagnostics.push(
      makeDiagnostic(
        "no-arc-support",
        0,
        Math.max(0.5, totalDuration || Math.max(...sortedFx.map((node) => node.endSec))),
        "unresolved",
        "FX intent exists without authored ARC support.",
        undefined,
        sortedFx.map((node) => node.id),
      ),
    );
  }

  return diagnostics
    .map((diagnostic) => ({
      ...diagnostic,
      startSec: clamp(diagnostic.startSec, 0, Math.max(diagnostic.startSec, totalDuration || diagnostic.endSec)),
      endSec: Math.max(diagnostic.startSec + 0.05, diagnostic.endSec),
      continuityScore: clamp(diagnostic.continuityScore, 0, 1),
    }))
    .sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);
}

export function summarizeContinuityDiagnostics(diagnostics: ContinuityDiagnostic[]): string {
  if (diagnostics.length === 0) return "No continuity diagnostics yet";

  const warnings = diagnostics.filter((diagnostic) => diagnostic.status !== "coherent");
  if (warnings.length === 0) return "Transitions read coherent across authored ARC and FX.";

  const counts = new Map<ContinuityStatus, number>();
  for (const warning of warnings) {
    counts.set(warning.status, (counts.get(warning.status) ?? 0) + 1);
  }

  const ranked = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([status, count]) => `${count} ${status}`);

  return `${warnings.length} continuity warning${warnings.length === 1 ? "" : "s"} across authored sonics${ranked.length ? `: ${ranked.join(", ")}` : ""}.`;
}

export function averageContinuityScore(diagnostics: ContinuityDiagnostic[]): number | null {
  if (diagnostics.length === 0) return null;

  const warnings = diagnostics.filter((diagnostic) => diagnostic.status !== "coherent");
  if (warnings.length === 0) return 0.92;

  const penalties = {
    abrupt: 0.2,
    unresolved: 0.22,
    mismatched: 0.17,
    dense: 0.14,
    gapped: 0.18,
    coherent: 0,
  } satisfies Record<ContinuityStatus, number>;

  const weightedPenalty = warnings.reduce((sum, diagnostic) => {
    const durationWeight = clamp(rangeDuration(diagnostic.startSec, diagnostic.endSec) / 3, 0.65, 1.35);
    return sum + penalties[diagnostic.status] * durationWeight;
  }, 0);

  return clamp(0.94 - weightedPenalty / (warnings.length + 0.75), 0.08, 0.92);
}